import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios from "../../axiosConfig";
import TrainerLayout from "./TrainerLayout";
import Tabs from "../commons/Tabs";
import "../../styles/DogDetails.css";
import TrainingSummaryList from "./TrainingSummaryList";
import AssignedExercisesList from "./AssignedExercisesList";
import TrainerNotesList from "./TrainerNotesList";
import DogSurveysList from "./DogSurveysList";
import DogReportsTab from "./DogReportsTab";
import DogInfoTab from "./DogInfoTab";
import InviteDogOwnerModal from "./InviteDogOwnerModal";
import { IoArrowBack } from "react-icons/io5";

const DogDetails = () => {
    const { t } = useTranslation(['dogs', 'common']);
    const { dogId } = useParams();
    const navigate = useNavigate();
    const [dog, setDog] = useState(null);
    const [owner, setOwner] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");
    const [invitationStatus, setInvitationStatus] = useState(null);
    const [showInviteModal, setShowInviteModal] = useState(false);

    useEffect(() => {
        const fetchDogDetails = async () => {
            try {
                const token = localStorage.getItem("jwt");

                // Fetch dog details
                const dogResponse = await axios.get(`/api/v1/dog/details/${dogId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                const dogData = dogResponse.data;
                setDog(dogData);

                // Fetch owner details if ownerId exists
                if (dogData.ownerId) {
                    const ownerResponse = await axios.get(`/api/v1/owner/details/${dogData.ownerId}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    setOwner(ownerResponse.data);
                } else {
                    // Fetch invitation status for ownerless dog
                    try {
                        const invResponse = await axios.get(`/api/v1/dog/invitation/${dogId}`, {
                            headers: { Authorization: `Bearer ${token}` },
                        });
                        setInvitationStatus(invResponse.data);
                    } catch (invErr) {
                        console.error("Could not fetch invitation status:", invErr);
                    }
                }
            } catch (err) {
                setError(t('dogs:fetchDogOrOwnerError'));
            } finally {
                setLoading(false);
            }
        };

        fetchDogDetails();
    }, [dogId]);

    const refreshDogData = useCallback(async () => {
        try {
            const token = localStorage.getItem("jwt");
            const dogResponse = await axios.get(`/api/v1/dog/details/${dogId}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const dogData = dogResponse.data;
            setDog(dogData);

            if (dogData.ownerId) {
                const ownerResponse = await axios.get(`/api/v1/owner/details/${dogData.ownerId}`, {
                    headers: { Authorization: `Bearer ${token}` },
                });
                setOwner(ownerResponse.data);
                setInvitationStatus(null);
            } else {
                setOwner(null);
                try {
                    const invResponse = await axios.get(`/api/v1/dog/invitation/${dogId}`, {
                        headers: { Authorization: `Bearer ${token}` },
                    });
                    setInvitationStatus(invResponse.data);
                } catch (invErr) {
                    console.error("Could not fetch invitation status:", invErr);
                }
            }
        } catch (err) {
            console.error("Error refreshing dog data:", err);
        }
    }, [dogId]);

    const handleInviteSent = (result) => {
        setShowInviteModal(false);
        refreshDogData();
    };

    const handleBackToPodopieczni = () => {
        navigate("/podopieczni");
    };

    if (loading) return <TrainerLayout><p>{t('common:loading')}</p></TrainerLayout>;
    if (error) return <TrainerLayout><p className="error">{error}</p></TrainerLayout>;

    return (
        <TrainerLayout>
            <div className="dog-details-container">
                {/* Back Arrow Button */}
                <button
                    className="dog-details-back-btn"
                    onClick={handleBackToPodopieczni}
                    aria-label={t('dogs:backToPupils')}
                >
                    <IoArrowBack />
                </button>

                <h1 className="dog-name">{dog?.name || t('dogs:unknownDog')}</h1>

                {/* No-owner section for ownerless dogs */}
                {dog && !dog.ownerId && (
                    <div className="dog-details-no-owner-section">
                        <div className="dog-details-no-owner-left">
                            <span className="dog-details-no-owner-badge">{t('dogs:noOwner')}</span>
                            {invitationStatus && invitationStatus.status === "PENDING" && (
                                <span className="dog-details-invitation-info">
                                    {t('dogs:pendingInvitation')}: {invitationStatus.email}
                                </span>
                            )}
                            {invitationStatus && invitationStatus.status === "EXPIRED" && (
                                <span className="dog-details-invitation-expired">
                                    {t('dogs:invitationExpired')} ({invitationStatus.email})
                                </span>
                            )}
                        </div>
                        <button
                            className="dog-details-invite-btn"
                            onClick={() => setShowInviteModal(true)}
                        >
                            {t('dogs:inviteOwner')}
                        </button>
                    </div>
                )}

                <Tabs
                    tabs={[
                        {
                            key: "dog_info",
                            label: t('dogs:tabs.dogInfo'),
                            endpoint: dog ? `/api/v1/dog/details/${dog.id}` : "",
                            paginated: false,
                            renderContent: (data) => (
                                <DogInfoTab
                                    dog={data}
                                    owner={owner}
                                    canEdit={!data.ownerId}
                                    onDogUpdated={refreshDogData}
                                    onDogDeleted={() => navigate("/podopieczni")}
                                />
                            ),
                        },
                        {
                            key: "recommendations",
                            label: t('dogs:tabs.recommendations'),
                            endpoint: dog ? `/api/v1/training-summary/dog/${dog.id}` : "",
                            paginated: true,
                            renderContent: (data) => <TrainingSummaryList summaries={data} dogId={dogId} />,
                        },
                        {
                            key: "exercises",
                            label: t('dogs:tabs.exercises'),
                            endpoint: dog ? `/api/v1/exercise/dog/${dog.id}/assigned` : "",
                            paginated: true,
                            renderContent: (data) => <AssignedExercisesList exercises={data} dogId={dogId} />,
                        },
                        {
                            key: "notes",
                            label: t('dogs:tabs.notes'),
                            endpoint: dog ? `/api/v1/trainer-note/dog/${dog.id}` : "",
                            paginated: true,
                            renderContent: (data) => <TrainerNotesList notes={data} dogId={dogId} />,
                        },
                        {
                            key: "surveys",
                            label: t('dogs:tabs.surveys'),
                            endpoint: dog ? `/api/v1/survey/dog/${dog.id}/assigned` : "",
                            paginated: false,
                            renderContent: (data) => <DogSurveysList surveys={data} dogId={dogId} canFill={!dog?.ownerId} />,
                        },
                        {
                            key: "reports",
                            label: t('dogs:tabs.reports'),
                            endpoint: dog && dog.id ? `/api/v1/reports/dog/${dog.id}/reports` : null,
                            paginated: false,
                            renderContent: (data) => <DogReportsTab dogReports={data || { assignments: [], submissions: [] }} dogId={dogId} canFill={!dog?.ownerId} />,
                        },
                    ]}
                    activeId={dogId}
                />
            </div>

            <InviteDogOwnerModal
                isOpen={showInviteModal}
                onClose={() => setShowInviteModal(false)}
                dogId={dogId}
                onInviteSent={handleInviteSent}
            />
        </TrainerLayout>
    );
};

export default DogDetails;
